import { Icon } from "@circle-react-shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";

export interface ItemLabelProps {
  description?: ReactNode;
  emptyLabel?: boolean;
  hideDescription?: boolean;
  id?: string; // TODO: Make it required
  isDisabled?: boolean;
  label?: ReactNode;
  labelClassName?: string;
  labelWrapperClassName?: string;
  name?: string;
  onToggleValue?: () => void;
  required?: boolean;
  shouldToggleValueOnLabelClick?: boolean;
  tooltipText?: ReactNode;
  tooltipTextClassName?: string;
  variant?: string;
}

export const ItemLabel = ({
  description = "",
  emptyLabel = false,
  hideDescription = false,
  id,
  isDisabled = false,
  label,
  labelClassName = "",
  labelWrapperClassName = "",
  name = "",
  required = false,
  shouldToggleValueOnLabelClick = false,
  onToggleValue,
  tooltipText = "",
  tooltipTextClassName = "",
  variant = "default",
}: ItemLabelProps) => {
  const { setValue, getValues } = useFormContext();

  const onLabelToggleValueClick = () => {
    if (!shouldToggleValueOnLabelClick || isDisabled) {
      return;
    }

    const hasValue = Boolean(getValues(name));
    setValue(name, !hasValue, { shouldDirty: true });
    onToggleValue?.();
  };

  return (
    <div
      className={classNames(
        anchorClassNames("form-meta"),
        legacyClassNames(`form-meta--${variant}`),
        labelWrapperClassName,
      )}
    >
      <label
        className={classNames(
          anchorClassNames("form-group__label"),
          "text-dark flex font-semibold",
          {
            "cursor-not-allowed opacity-20": isDisabled,
          },
          labelClassName,
        )}
        data-testid="item-label"
        {...(shouldToggleValueOnLabelClick && {
          onClick: onLabelToggleValueClick,
        })}
        htmlFor={id}
      >
        {label ? (
          <span>
            {label}
            {required && (
              <TippyV2 content={t("required_field")}>
                <span className="text-red-500">*</span>
              </TippyV2>
            )}
            {Boolean(tooltipText) && (
              <TippyV2
                content={tooltipText}
                tooltipTextClassName={tooltipTextClassName}
                className="inline-flex"
              >
                <Icon
                  type="question-mark"
                  className={classNames(
                    anchorClassNames("icon"),
                    legacyClassNames("icon--xs"),
                    "ml-1.5",
                  )}
                />
              </TippyV2>
            )}
          </span>
        ) : (
          emptyLabel && <>&nbsp;</>
        )}
      </label>
      {description && !hideDescription && (
        <div
          className={classNames(anchorClassNames("field-description"), {
            "cursor-not-allowed opacity-20": isDisabled,
          })}
        >
          {description}
        </div>
      )}
    </div>
  );
};
